import { Box, Fade, IconButton, Modal, Typography } from "@mui/material";
import React, { FC, useMemo } from "react";
import { theme } from "../../theme";
import useViewport from "../../hooks/useViewport";
import getREMFromPX from "../../utils/getREMFromPX";
import CloseIcon from "@mui/icons-material/Close";
import useGetThemePath from "../../hooks/useGetThemePath";

interface CustomModalProps {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  title?: string;
}

export const ProfileSettingsModal: FC<CustomModalProps> = ({
  open,
  onClose,
  children,
  title,
}) => {
  const themePath = useGetThemePath();
  const { colors } = theme[themePath];
  const { isMobile } = useViewport();

  // Define styles that conform to MUI's SxProps type
  const modalStyles = useMemo(
    () => ({
      modal: {
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        margin: "0 15.5px",
        paddingTop: { xs: "2.5%", md: "0" },
      },
      paper: {
        backgroundColor: colors.white || "#ffffff",
        // Fix TypeScript error with proper responsive type formatting
        maxWidth: { xs: "100%", md: getREMFromPX(900) },
        width: "100%",
        flexGrow: 0,
        boxSizing: "border-box" as const,
        borderRadius: getREMFromPX(20),
        outline: "none",
        boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.25)",
        position: "relative",
        overflow: "hidden",
        maxHeight: { xs: "95vh", md: "80%" },
        height: { xs: "95vh", md: "auto" },
        padding: !isMobile ? "80px 40px 30px" : "16px 16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      },
      closeButton: {
        position: "absolute",
        top: 15,
        right: 15,
        color: colors.textPrimary.headingAlt,
      },
    }),
    [colors, isMobile]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={modalStyles.modal}
    >
      <Fade in={open}>
        <Box sx={modalStyles.paper}>
          {!!title && (
            <Typography
              sx={{
                position: "absolute",
                top: {
                  md: 30,
                  xs: 14,
                },
                pl: 3,
                fontFamily: "Inter",
                fontSize: getREMFromPX(20),
                fontWeight: "500",
                fontStyle: "normal",
                letterSpacing: 0,
                textAlign: "left",
                color: colors.textPrimary.heading,
                height: 40,
                width: "100%",
                backgroundColor: colors.white || "#ffffff",
              }}
            >
              {title}
            </Typography>
          )}
          <IconButton
            sx={{
              position: "absolute",
              top: {
                md: 15,
                xs: 6,
              },
              right: 15,
              color: colors.links.teal,
            }}
            onClick={onClose}
            data-cy="profile-mydetails-close"
          >
            <CloseIcon
              sx={{ fontWeight: "bolder" }}
              fill={colors.icons.default.fillColor}
              fontSize="large"
            />
          </IconButton>
          {children}
        </Box>
      </Fade>
    </Modal>
  );
};
